import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { APP_PAGES, PAGE_LAYOUT, PLATFORMS } from './config/const.config';
import SplashScreenPage from './views/SplashScreen';
import HomePage from './views/Home';
import SearchPage from './views/Search';
import SettingsPage from './views/Settings';
import { getMainMenuData} from './services/channelData.service';
import './utils/webFocusHandler.util';
import './assets/styles/chota.css';
import './assets/styles/base.css';
import './assets/styles/web.css';
import {
  addLocalStorageData,
  getLocalStorageData,
} from './utils/localCache.util';
import DetailPageWrapper from './components/common/DetailPageWrapper';
import FeaturedPlayerWrapper from './components/common/FeaturedPlayerWrapper';
import SeeMoreDetailsPage from './components/common/SeeMoreDetailsPage';
import SeriesDetailPageWrapper from './components/common/SeriesDetailPageWrapper';
import SeriesSeeMoreDetailsPage from './components/common/SeriesSeeMoreDetailsPage';

const App = () => {
  const [appLoaded, setAppLoaded] = useState(false);
  const [menuData, setMenuData] = useState([]);
  const [pageLayouts, setPageLayouts] = useState({
    layout: PAGE_LAYOUT.GRID,
    bgVideo: '',
  });
  const [activePage, setActivePage] = useState(APP_PAGES.LANDINGPAGE);
  const [activePageLayout, setActivePageLayout] = useState({
    layout: PAGE_LAYOUT.GRID,
    bgVideo: '',
  });

  useEffect(() => {
    getMainMenuData()
      .then((res) => {
        const menu = [];
        const layouts = {};
        res.content.top_menus.forEach((m) => {
          menu.push({
            id: m._id,
            title: m.title,
          });

          layouts[m._id] = {
            layout: m.playlist_layout,
            bgVideo: '',
          };
          if (
            Number(m.live_video_in_background) === 1 &&
            m.live_video_link !== ''
          ) {
            layouts[m._id].bgVideo = m.live_video_link;
          }
        });

        setMenuData(menu);
        setPageLayouts(layouts);
        if (PLATFORMS.FIRETV || PLATFORMS.SAMSUNG || PLATFORMS.LG) {
          setActivePage(menu[0].id.toString());
        }
        const getLocalData = getLocalStorageData('pageClick');
        if (menu.length === 0) {
          setActivePage(menu[0]?.id.toString());
          return;
        }

        const currentMenuName = window.location.pathname.split('/')[1];
        if (currentMenuName) {
          const normalize = str => str.toLowerCase().replace(/[:\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
          const matchedMenu = menu.find(item =>
            normalize(item.title) === normalize(currentMenuName)
          );
          if (matchedMenu) {
            setActivePage(matchedMenu.id);
          } else if (getLocalData) {
            setActivePage(menu[0].id.toString());
          }
        } else {
          setActivePage(menu[0].id.toString());
        }
        setActivePageLayout(layouts[menu[0].id]);

        setTimeout(() => {
          setAppLoaded(true);
        }, 1000);
      })
      .catch(() => {});
  }, []);

  const handlePageChange = (page) => {
    addLocalStorageData('pageClick', page);

    window.scrollTo(0, 0);
    if (pageLayouts[Number(page)]) {
      setActivePageLayout(pageLayouts[Number(page)]);
    } else {
      setActivePageLayout(PAGE_LAYOUT.GRID);
    }
    setActivePage(page.toString());
  };

  if (!appLoaded) {
    return <SplashScreenPage />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to={`/${menuData[0].title.toLowerCase().replace(/\s+/g, '_').replace(/[^\w-]/g, '')}`} />} />
        <Route 
          path="/search"
          element={
            <SearchPage
              menuData={menuData}
              activePage={activePage}
              activeSubPage={null}
              handlePageChange={handlePageChange}
            />
          }
        />
        <Route 
          path="/settings"
          element={
            <SettingsPage
              menuData={menuData}
              activePage={activePage}
              handlePageChange={handlePageChange}
            />
          }
        />
        <Route 
          path="/:playlistTitle" 
          element={
            <HomePage
              key={activePage}
              menuData={menuData}
              activePage={activePage}
              activePageLayout={activePageLayout}
              handlePageChange={handlePageChange}
            />
          }
        />
        <Route
          path="/:playlistTitle/:categoryTitle/:videoTitle"
          element={
            <DetailPageWrapper
              activePage={activePage}
              menuData={menuData}
              activePageLayout={activePageLayout}
              handlePageChange={handlePageChange}
              landingPageButton={menuData[0].id}
            />
          }
        />
        <Route 
          path="/watch/featured/:videoTitle"
          element={
            <FeaturedPlayerWrapper
              activePage={activePage} 
            />
          }
        />
        <Route 
          path="/channels/details/:seeMoreTitle"
          element={
            <SeeMoreDetailsPage
              activePage={activePage}
              activePageLayout={activePageLayout}
              handlePageChange={handlePageChange}
              menuData={menuData}
            />
          }
        />
        <Route
            path="/:playlistTitle/series/:categoryTitle/:seriesTitle"
            element={
              <SeriesDetailPageWrapper
                activePage={activePage}
                menuData={menuData}
                activePageLayout={activePageLayout}
                handlePageChange={handlePageChange}
                landingPageButton={menuData[0].id}
              />
            }
          />
        <Route 
          path="/series/details/:seeMoreTitle"
          element={
            <SeriesSeeMoreDetailsPage
              activePage={activePage}
              activePageLayout={activePageLayout}
              handlePageChange={handlePageChange}
              menuData={menuData}
            />
          }
        />
        <Route path="*" element={<Navigate to={`/${menuData[0].title.toLowerCase().replace(/\s+/g, '_').replace(/[^\w-]/g, '')}`} />} />
      </Routes>
    </Router>
  );
};

export default App;
